import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { CheckCircle, Package, ArrowRight, Loader2 } from 'lucide-react';
import { cmsApi } from '../api/requests';

export default function CheckoutSuccess() {
  const location = useLocation();
  const { orderDetails, product, customerInfo } = location.state || {};
  const [cms, setCms] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCms = async () => {
      try {
        const { data } = await cmsApi.getSection('checkout');
        setCms(data);
      } catch (err) {
        console.error('Failed to fetch CMS:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCms();
  }, []);

  const sp = cms?.successPage || {
    title: 'Order Confirmed!',
    message: "Thank you for your purchase. We've received your order and will begin processing it shortly.",
    nextStepsTitle: 'What happens next?',
    nextStepsMessage: "You'll receive a confirmation email with your order details. We'll contact you via Telegram when your figurine is ready to ship.",
    continueButtonText: 'Continue Shopping',
    homeButtonText: 'Back to Home'
  };

  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="w-8 h-8 text-indigo-400 animate-spin" /></div>;
  }

  return (
    <div data-testid="checkout-success-page" className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4 pt-24">
      <div className="max-w-lg w-full text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-500/20 rounded-full mb-8">
          <CheckCircle className="w-10 h-10 text-emerald-400" />
        </div>

        <h1 className="text-3xl font-['Outfit'] font-medium text-white mb-4">{sp.title}</h1>
        <p className="text-zinc-400 mb-8">{sp.message}</p>

        {orderDetails && (
          <div className="bg-[#121212] border border-white/10 rounded-xl p-6 mb-8 text-left">
            <h3 className="text-white font-medium mb-4">Order Details</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-zinc-400">Order ID:</span>
                <span className="text-white font-mono text-sm">{orderDetails.id}</span>
              </div>
              {product && (
                <div className="flex justify-between">
                  <span className="text-zinc-400">Product:</span>
                  <span className="text-white">{product.title}</span>
                </div>
              )}
              {product && (
                <div className="flex justify-between">
                  <span className="text-zinc-400">Amount:</span>
                  <span className="text-white">&euro;{product.priceEUR.toFixed(2)}</span>
                </div>
              )}
              {customerInfo && (
                <div className="flex justify-between">
                  <span className="text-zinc-400">Email:</span>
                  <span className="text-white">{customerInfo.email}</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="p-6 bg-indigo-500/10 border border-indigo-500/20 rounded-xl mb-8">
          <div className="flex items-center justify-center space-x-3 text-indigo-400 mb-2">
            <Package className="w-5 h-5" />
            <span className="font-medium">{sp.nextStepsTitle}</span>
          </div>
          <p className="text-zinc-400 text-sm">{sp.nextStepsMessage}</p>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/catalog" data-testid="continue-shopping-btn"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-indigo-500 hover:bg-indigo-600 text-white font-medium rounded-lg transition-colors">
            <span>{sp.continueButtonText}</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/" className="text-zinc-400 hover:text-white transition-colors">
            {sp.homeButtonText || 'Back to Home'}
          </Link>
        </div>
      </div>
    </div>
  );
}
